import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, TrendingUp, Target } from "lucide-react";
import { DashboardMockup } from "./DashboardMockup";
import { LeadDialog } from "./LeadDialog";
import { MaskedLine, WHATSAPP_URL, WhatsAppIcon, EASE } from "./shared";

const TiltDashboard = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 120, damping: 18 });
  const sy = useSpring(my, { stiffness: 120, damping: 18 });
  const rotateY = useTransform(sx, [-0.5, 0.5], [7, -7]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [-5, 5]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      initial={{ opacity: 0, y: 70, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
      style={{ rotateX, rotateY, transformPerspective: 1400 }}
      className="relative mx-auto w-full max-w-5xl will-change-transform"
    >
      <div className="absolute -inset-10 -z-10 rounded-[40px] bg-gradient-to-tr from-tria-vivid/25 via-tria-blue/10 to-tria-sky/20 blur-3xl" />
      <DashboardMockup live />

      <motion.div
        data-testid="hero-chip-margem"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 1.5, ease: EASE }}
        className="glass-card absolute -right-4 -top-8 hidden animate-float rounded-2xl px-5 py-4 shadow-2xl md:block lg:-right-14"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-tria-vivid/20 text-tria-sky">
            <TrendingUp className="h-5 w-5" />
          </span>
          <div>
            <p className="text-[10px] font-medium text-tria-subtle">Margem de contribuição</p>
            <p className="font-mono text-lg font-bold text-white">48,6% <span className="text-[11px] font-semibold text-emerald-400">+3,4 p.p.</span></p>
          </div>
        </div>
      </motion.div>

      <motion.div
        data-testid="hero-chip-equilibrio"
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 1.7, ease: EASE }}
        className="glass-card absolute -bottom-8 -left-4 hidden animate-float rounded-2xl px-5 py-4 shadow-2xl [animation-delay:1.2s] md:block lg:-left-14"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-400/15 text-emerald-400">
            <Target className="h-5 w-5" />
          </span>
          <div>
            <p className="text-[10px] font-medium text-tria-subtle">Ponto de equilíbrio</p>
            <p className="font-mono text-lg font-bold text-white">Dia 14 <span className="text-[11px] font-semibold text-emerald-400">atingido</span></p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      <motion.div style={{ y: yBg }} className="pointer-events-none absolute inset-0">
        <div className="hero-grid absolute inset-0" />
        <div className="absolute left-1/2 top-[-320px] h-[640px] w-[900px] -translate-x-1/2 rounded-full bg-tria-vivid/20 blur-[140px]" />
        <div className="absolute right-[-200px] top-[30%] h-[420px] w-[420px] rounded-full bg-tria-sky/10 blur-[120px]" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative mx-auto max-w-7xl px-5 pb-20 pt-16 sm:px-8 sm:pt-24 lg:pb-28">
        <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-tria-sky/25 bg-tria-sky/[0.07] px-4 py-1.5"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-tria-sky" />
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.22em] text-tria-sky">
              Plataforma + método gerencial próprio
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-[-0.025em] text-white sm:text-6xl lg:text-7xl" data-testid="hero-title">
            <MaskedLine delay={0.25}>Gestão inteligente</MaskedLine>
            <MaskedLine delay={0.4}>
              de <span className="text-gradient-blue">margem por lotes</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-2xl text-base leading-relaxed text-tria-mist sm:text-lg"
            data-testid="hero-subtitle"
          >
            A TRIA transforma compras, precificação, remarcações, vendas e despesas em
            indicadores claros de resultado. Uma plataforma que conecta{" "}
            <span className="font-semibold text-white">Custos × Margem × Faturamento</span>{" "}
            para mostrar se o negócio está realmente gerando lucro.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-col items-center gap-3.5 sm:flex-row"
          >
            <a
              data-testid="hero-cta-demo"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="glow-blue group inline-flex items-center gap-2.5 rounded-full bg-tria-vivid px-8 py-4 text-[15px] font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:bg-tria-blue"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Agendar demonstração
            </a>
            <Link
              data-testid="hero-cta-explore"
              to="/metodo"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-[15px] font-semibold text-white transition-all duration-300 hover:border-tria-sky/50 hover:bg-white/5"
            >
              Ver o método TRIA
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="mt-5 text-[13px] text-tria-subtle"
          >
            Prefere que a gente te chame?{" "}
            <LeadDialog
              trigger={
                <button data-testid="hero-cta-lead" className="font-semibold text-tria-sky underline decoration-tria-sky/40 underline-offset-4 transition-colors hover:text-white">
                  Deixe seus dados aqui
                </button>
              }
            />
          </motion.p>
        </div>

        <div className="mt-16 sm:mt-20">
          <TiltDashboard />
        </div>
      </motion.div>
    </section>
  );
};
